export type CertificatePartKind =
  | 'legal'
  | 'privacy'
  | 'identity'
  | 'verification'

export type CertificatePart = {
  id: string
  title: string
  what: string
  why: string
  whyLink?: { label: string; href: string }
  kind: CertificatePartKind
  /** Region of the certificate image this part occupies, as fractions of width/height. */
  region: { x: number; y: number; w: number; h: number }
  /** Where the numbered marker sits on the certificate image, as fractions. */
  dot: { x: number; y: number }
}

export const partKindMeta = {
  legal: {
    label: 'The legal bit',
    dotClassName: 'bg-[#EF8E3B]',
    labelClassName: 'text-[#D97B1F]',
  },
  privacy: {
    label: 'Privacy',
    dotClassName: 'bg-[#7C5CD6]',
    labelClassName: 'text-[#5B3FA8]',
  },
  identity: {
    label: 'Who is behind it',
    dotClassName: 'bg-[#5A6478]',
    labelClassName: 'text-[#5A6478]',
  },
  verification: {
    label: 'Anti-fraud',
    dotClassName: 'bg-[#2563EB]',
    labelClassName: 'text-[#2563EB]',
  },
} as const satisfies Record<
  CertificatePartKind,
  { label: string; dotClassName: string; labelClassName: string }
>

export const certificateParts: CertificatePart[] = [
  {
    id: 'clinic-details',
    title: 'The clinic details',
    what: 'Our logo, operating states, and the clinic’s contact details.',
    why: 'A certificate has to say who issued it. Anyone holding one can contact the clinic and confirm it came from us.',
    kind: 'identity',
    region: { x: 0.012, y: 0.008, w: 0.976, h: 0.14 },
    dot: { x: 0.935, y: 0.075 },
  },
  {
    id: 'certificate-type',
    title: 'The certificate type',
    what: 'A Medical Certificate — or a Carer’s Certificate when you’re looking after someone else.',
    why: 'Personal leave and carer’s leave are separate entitlements under the National Employment Standards, so the certificate states which one it supports.',
    kind: 'legal',
    region: { x: 0.22, y: 0.222, w: 0.56, h: 0.058 },
    dot: { x: 0.78, y: 0.251 },
  },
  {
    id: 'issue-date',
    title: 'The issue date',
    what: 'The date the doctor approved the certificate after their assessment.',
    why: 'It shows the assessment happened at the time of the illness — the first thing an employer checks against the absence.',
    kind: 'legal',
    region: { x: 0.025, y: 0.316, w: 0.3, h: 0.038 },
    dot: { x: 0.325, y: 0.335 },
  },
  {
    id: 'the-statement',
    title: 'The statement',
    what: 'Who the certificate covers, what they’re unfit for — work or study — and the exact dates, inclusive.',
    why: 'The Fair Work Act asks for evidence that would satisfy a reasonable person — the name, the capacity and the dates are exactly that.',
    kind: 'legal',
    region: { x: 0.025, y: 0.385, w: 0.94, h: 0.072 },
    dot: { x: 0.94, y: 0.409 },
  },
  {
    id: 'the-reason',
    title: 'The reason',
    what: '“Due to a medical condition” — and nothing more.',
    why: 'You don’t have to tell your employer your diagnosis — every certificate we issue uses this same standard wording. Adding a diagnosis takes a general consultation.',
    whyLink: {
      label: 'Book a general consultation',
      href: '/telehealth-consultation',
    },
    kind: 'privacy',
    region: { x: 0.025, y: 0.421, w: 0.42, h: 0.036 },
    dot: { x: 0.445, y: 0.439 },
  },
  {
    id: 'signature',
    title: 'The signature',
    what: 'The certifying doctor signs every certificate, as they would on paper.',
    why: 'A certificate is the doctor’s formal opinion, and the signature binds them to it. Unsigned certificates are routinely rejected.',
    kind: 'legal',
    region: { x: 0.025, y: 0.487, w: 0.42, h: 0.135 },
    dot: { x: 0.445, y: 0.554 },
  },
  {
    id: 'the-doctor',
    title: 'The doctor',
    what: 'Their name, qualifications and Medicare provider number.',
    why: 'Only a registered practitioner can issue one. The provider number ties the certificate to an AHPRA-registered doctor anyone can look up.',
    kind: 'identity',
    region: { x: 0.025, y: 0.636, w: 0.42, h: 0.082 },
    dot: { x: 0.445, y: 0.677 },
  },
  {
    id: 'verification',
    title: 'The verification footer',
    what: 'A unique reference number, a verification link, a QR code and our support email.',
    why: 'Scanning the QR code — or entering the reference on our site — shows in seconds whether we issued it. No phone call needed.',
    kind: 'verification',
    region: { x: 0.012, y: 0.822, w: 0.976, h: 0.17 },
    dot: { x: 0.935, y: 0.845 },
  },
]
